/**
 * Admin-only Integration Health page (iter212a).
 *
 * Shows live diagnostics for the Garage Hive integration — token
 * status, canary read, 24h counters and the last 15 audit events —
 * so an admin can spot silent auth / config failures without tailing
 * server logs.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  RefreshCw, Home, ShieldCheck, ShieldAlert, ShieldX,
  Clock, Cable, Building2, Loader2, Copy, Check, ExternalLink,
  Download, Package,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_STYLE = {
  green: "bg-emerald-50 border-emerald-300 text-emerald-800",
  amber: "bg-amber-50 border-amber-300 text-amber-800",
  red:   "bg-red-50 border-red-300 text-red-800",
};

const OUTCOME_ICON = {
  green: ShieldCheck,
  amber: ShieldAlert,
  red:   ShieldX,
};

export default function AdminIntegrationHealthPage() {
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefresh] = useState(false);
  const [data, setData]         = useState(null);
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/integrations/garage-hive/health`);
      setData(res.data);
      setError(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't load integration health.");
    } finally {
      setLoading(false);
      setRefresh(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doRefresh = () => {
    setRefresh(true);
    load();
  };

  // iter212i.c — Preview Fault-Lab source pack. Admin-only download
  // through the signed-in session so no token or password ever leaves
  // this browser. Uses axios (which carries the JWT from
  // AuthContext.defaults) with responseType=blob and triggers a
  // client-side download via a synthetic anchor.
  const [packDownloading, setPackDownloading] = useState(false);
  const [packMeta, setPackMeta] = useState({
    sha256: "c48f7de4607c9f859a7c3053766d5a0bdf9385b4f6a8eb21438bb6e23998691f",
    size_bytes: 32675,
    commit: "dc3bbd9",
  });
  const downloadFaultLabPack = async () => {
    setPackDownloading(true);
    try {
      const res = await axios.get(
        `${API}/downloads/gh_preview_fault_lab_admin.zip`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gh_preview_fault_lab.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Compute SHA-256 of the downloaded blob so the operator can
      // verify integrity without leaving the page.
      try {
        const buf = await blob.arrayBuffer();
        const digest = await crypto.subtle.digest("SHA-256", buf);
        const hex = Array.from(new Uint8Array(digest))
          .map(b => b.toString(16).padStart(2, "0")).join("");
        setPackMeta(prev => ({ ...prev, sha256: hex, size_bytes: buf.byteLength }));
        toast.success("Pack downloaded — SHA-256 verified in-page.");
      } catch (_) {
        toast.success("Pack downloaded.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Download failed.";
      toast.error(typeof detail === "string" ? detail : "Download failed.");
    } finally {
      setPackDownloading(false);
    }
  };

  // AL Dev Sandbox — expose the current /gh/open base URL and a few
  // ready-to-copy example URLs so the Garage Hive AL developer can
  // verify they're pointing at the right environment (Preview vs Prod).
  const ghOpenBase = `${window.location.origin}/gh/open`;
  const alExamples = [
    { label: "By jobsheet number",  url: `${ghOpenBase}?mode=foh&jobsheet_no=SJ027002` },
    { label: "By vehicle registration", url: `${ghOpenBase}?mode=foh&reg=AB12CDE` },
    { label: "By customer name",    url: `${ghOpenBase}?mode=foh&customer_name=Smith` },
    { label: "Technician mode",     url: `${ghOpenBase}?mode=technician&jobsheet_no=SJ027002` },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-700" />
      </div>
    );
  }

  const overall = data?.overall || (error ? "red" : "amber");
  const Icon    = OUTCOME_ICON[overall] || ShieldAlert;
  const badge   = OUTCOME_STYLE[overall] || OUTCOME_STYLE.amber;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="admin-integration-health-page">
      <div className="max-w-5xl mx-auto px-5 py-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="inline-flex items-center gap-1.5 mb-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
              <Cable className="w-3 h-3 text-slate-600" />
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-700 font-semibold">
                Admin · Integration Health
              </p>
            </div>
            <h1 className="text-[26px] sm:text-[30px] font-bold tracking-tight text-slate-900"
                data-testid="ih-title">
              Garage Hive
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              Live status of the read-only pipeline from Business Central.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={doRefresh} disabled={refreshing}
                    data-testid="ih-refresh">
              {refreshing
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Refresh
            </Button>
            <Button variant="outline" onClick={() => navigate("/app/foh")}
                    data-testid="ih-back">
              <Home className="w-4 h-4 mr-1.5" />
              FOH
            </Button>
          </div>
        </div>

        {/* Overall banner */}
        <div className={`rounded-xl border-2 px-5 py-4 mb-5 flex items-center gap-4 ${badge}`}
             data-testid={`ih-overall-${overall}`}>
          <Icon className="w-8 h-8 shrink-0" />
          <div>
            <p className="text-xs uppercase font-bold tracking-wider">Overall</p>
            <p className="text-lg font-semibold">
              {data?.message || (error ? error : "Unknown state")}
            </p>
          </div>
        </div>

        {/* AL Dev Sandbox URL — expose the current /gh/open base URL
            so the Garage Hive AL developer can point their extension
            at the right environment (Preview vs Prod). */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
          data-testid="ih-al-sandbox"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">
                AL Extension Sandbox URL
              </h2>
              <p className="text-[11px] text-slate-500 leading-snug max-w-md">
                Point the Garage Hive AL &quot;Open in Yestimate&quot; action at this base URL
                for the current environment. Copy any example to test end-to-end.
              </p>
            </div>
            <a
              href="/docs/GARAGE_HIVE_AL_INTEGRATION_CONTRACT.md"
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-teal-700 hover:text-teal-800 inline-flex items-center gap-1"
              data-testid="ih-al-contract-link"
            >
              Integration contract <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <CopyRow
            label="Base URL"
            value={ghOpenBase}
            testId="ih-al-base"
            mono
          />

          <div className="mt-3 space-y-2">
            {alExamples.map((ex) => (
              <CopyRow
                key={ex.label}
                label={ex.label}
                value={ex.url}
                testId={`ih-al-example-${ex.label.toLowerCase().replace(/\s+/g, "-")}`}
                mono
                small
              />
            ))}
          </div>
        </section>

        {/* iter212i.c — Preview Fault-Lab source pack (admin-only,
            served through the signed-in session — no public URL, no
            token pasting). */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
          data-testid="ih-fault-lab-pack"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1 flex items-center gap-1.5">
                <Package className="w-3 h-3" />
                Preview Fault-Lab Pack (iter212i)
              </h2>
              <p className="text-[11px] text-slate-500 leading-snug max-w-xl">
                Playbook + fault-lab source + regression tests for the projection retry
                acceptance suite. Uses the current admin session — the ZIP is streamed
                through the browser, never through a public URL. Faults stay disarmed;
                downloading does not touch any fixture.
              </p>
            </div>
            <Button
              size="sm"
              onClick={downloadFaultLabPack}
              disabled={packDownloading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="ih-fault-lab-download-btn"
            >
              {packDownloading
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Download ZIP
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
            <KV label="Preview commit" value={packMeta.commit.slice(0, 12) + "…"} mono />
            <KV label="Size (bytes)"   value={packMeta.size_bytes.toLocaleString()} mono />
            <KV label="Expected SHA-256"
                value={packMeta.sha256.slice(0, 16) + "…" + packMeta.sha256.slice(-8)}
                mono />
          </div>
          <p className="mt-3 text-[10px] text-slate-500 leading-relaxed">
            After download the button computes the SHA-256 of the received bytes in-page.
            The hash above updates to the verified value on success — match it against the
            expected hash Codex shared to confirm integrity.
          </p>
          <div className="mt-3 pt-3 border-t border-slate-200">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/app/admin/gh-preview-fault-lab")}
              data-testid="ih-fault-lab-console-btn"
            >
              Open Fault-Lab Console →
            </Button>
            <span className="ml-3 text-[11px] text-slate-500">
              Arm / disarm / seed / emit / cleanup through the signed-in admin session — no
              token extraction needed.
            </span>
          </div>
        </section>

        {/* Config row */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <KV label="GH BC environment"  value={data?.environment || "—"} testId="ih-env"
              hint="Business Central tenant environment name in the GH REST base URL — NOT the Yestimate deployment environment." />
          <KV label="Tenant ID"          value={data?.tenant_id_masked || "—"} mono />
          <KV label="Client ID"          value={data?.client_id_masked || "—"} mono />
          <KV label="Token"              value={data?.token_ok
                                                  ? `OK · expires in ${data.token_expires_in ?? "?"}s`
                                                  : "Not acquired"}
              tone={data?.token_ok ? "good" : "bad"}
              testId="ih-token" />
        </section>

        {/* Canary block */}
        <section className={`bg-white rounded-xl border p-5 mb-5 shadow-sm ${
          data?.canary_ok ? "border-emerald-200" : "border-red-200"
        }`} data-testid="ih-canary">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Canary Read (top=1 jobsheet)
          </h2>
          <p className={`text-sm font-semibold ${
            data?.canary_ok ? "text-emerald-700" : "text-red-700"
          }`}>
            {data?.canary_ok ? "✔ OK" : "✖ Failed"} · {data?.canary_detail || ""}
          </p>
          {data?.technical && (
            <pre className="mt-2 text-[11px] leading-relaxed bg-slate-900 text-slate-100 rounded p-3 overflow-auto">
              {data.technical}
            </pre>
          )}
        </section>

        {/* Counters */}
        {data?.counters_24h && Object.keys(data.counters_24h).length > 0 && (
          <section className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm"
                   data-testid="ih-counters">
            <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
              Last 24 hours
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(data.counters_24h).map(([key, n]) => {
                const bad = /failed|missing|ambiguous|not_found|auth_failed/i.test(key);
                return (
                  <div key={key}
                       className={`rounded-lg border px-3 py-2 ${
                         bad ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"
                       }`}>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                      {key}
                    </div>
                    <div className={`text-xl font-bold ${
                      bad ? "text-red-700" : "text-slate-800"
                    }`}>
                      {n}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Recent events */}
        <section className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
                 data-testid="ih-events">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">
            Recent audit events
          </h2>
          {(data?.recent_events || []).length === 0 ? (
            <p className="text-sm text-slate-400 italic">No events yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {(data.recent_events || []).map((e) => {
                const bad = (e.outcome && e.outcome !== "ok");
                return (
                  <li key={e.id} className="py-2 flex items-start gap-3">
                    {bad
                      ? <ShieldAlert className="w-4 h-4 mt-0.5 text-red-500 shrink-0" />
                      : <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 justify-between">
                        <span className="font-semibold text-slate-800">{e.event}</span>
                        <span className="text-[11px] text-slate-400 shrink-0 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(e.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-slate-600 text-xs mt-0.5">
                        {e.jobsheet_number && <span><Building2 className="inline w-3 h-3 mr-1"/>{e.jobsheet_number}</span>}
                        {e.user_email && <span className="ml-3 text-slate-500">{e.user_email}</span>}
                        {e.outcome && e.outcome !== "ok" && (
                          <span className="ml-3 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold">
                            {e.outcome}
                          </span>
                        )}
                      </div>
                      {e.detail && (
                        <div className="text-[11px] text-slate-500 mt-1 truncate" title={e.detail}>
                          {e.detail}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <p className="text-[11px] text-slate-400 text-center mt-4">
          Checked at {data?.checked_at ? new Date(data.checked_at).toLocaleString() : "—"}
        </p>
      </div>
    </div>
  );
}

function KV({ label, value, tone, mono, testId, hint }) {
  const toneCls = tone === "good"
    ? "text-emerald-700"
    : tone === "bad" ? "text-red-700" : "text-slate-800";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testId}
         title={hint || undefined}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </div>
      <div className={`text-sm font-semibold mt-0.5 ${toneCls} ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
      {hint && (
        <div className="text-[10px] text-slate-500 leading-snug mt-1">{hint}</div>
      )}
    </div>
  );
}

function CopyRow({ label, value, testId, mono, small }) {
  const [copied, setCopied] = useState(false);

  const doCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback for insecure contexts / older browsers
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed — select the URL manually");
    }
  };

  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
      data-testid={testId}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          {label}
        </div>
        <div
          className={`${small ? "text-xs" : "text-sm"} text-slate-800 truncate ${mono ? "font-mono" : ""}`}
          title={value}
        >
          {value}
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={doCopy}
        className="shrink-0 h-8 px-2"
        data-testid={`${testId}-copy`}
      >
        {copied
          ? <Check className="w-3.5 h-3.5 text-emerald-600" />
          : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
}
